/**
 * Authentication API Service
 *
 * Focused service for authentication-related API operations with security enhancements.
 *
 * SECURITY: Uses httpOnly cookies for token storage (prevents XSS attacks).
 * Tokens are automatically sent with requests via cookies - no manual token handling needed.
 */

import { apiClient, handleAPIError } from "./apiClient";
import { isAxiosError } from "axios";
import type { RegistrationStatus } from "../types/api";
import type {
  CareCoordinationAgencyRegistrationRequest,
  CareCoordinationAgencyRegistrationResponse,
} from "../types/auth";

// Auth API functions
export const authAPI = {
  // Login - tokens are stored in httpOnly cookies by backend
  login: async (credentials: {
    email: string;
    password: string;
    rememberMe?: boolean;
  }): Promise<any> => {
    try {
      // Convert to form-encoded format expected by OAuth2PasswordRequestForm
      const formData = new URLSearchParams();
      formData.append("username", credentials.email);
      formData.append("password", credentials.password);

      // Optional remember-me flag to control refresh token lifetime on backend.
      // We pass it as a query parameter so the backend doesn't need to re-read the form body.
      const url =
        typeof credentials.rememberMe === "boolean"
          ? `/auth/login?remember_me=${
              credentials.rememberMe ? "true" : "false"
            }`
          : "/auth/login";

      const response = await apiClient.post(url, formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      // SECURITY: Tokens are stored in httpOnly cookies by the backend
      // Do NOT store tokens in localStorage/sessionStorage (XSS vulnerability)
      // Cookies are automatically sent with subsequent requests
      // Response may or may not include tokens in body depending on USE_COOKIE_ONLY_TOKENS config

      return response.data;
    } catch (error) {
      // Preserve Axios error so callers can map 422 (e.g. missing password) to clear copy.
      throw error;
    }
  },

  // Refresh token - gets new access token using refresh token from cookie
  refresh: async (): Promise<any> => {
    try {
      const response = await apiClient.post("/auth/refresh");
      // New tokens are automatically set in httpOnly cookies by backend
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        throw error;
      }
      throw new Error(handleAPIError(error));
    }
  },

  // Register patient
  registerPatient: async (patientData: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    date_of_birth?: string;  // optional — progressive profiling (collected on profile page)
    gender?: string;
    email_verification_token?: string;
  }): Promise<any> => {
    try {
      const response = await apiClient.post(
        "/auth/register/patient",
        patientData
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        throw error;
      }
      throw new Error(handleAPIError(error));
    }
  },

  /** Invited org teammate signup (provider_staff + accept invite). */
  registerProviderStaffFromInvite: async (staffData: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    invitation_token: string;
    email_verification_token?: string;
    accepted_terms?: boolean;
  }): Promise<any> => {
    try {
      const response = await apiClient.post(
        "/auth/register/provider-staff",
        staffData
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        throw error;
      }
      throw new Error(handleAPIError(error));
    }
  },

  // Send 6-digit verification code to email (for registration – provider, social worker, or patient)
  /** Resend OTP for existing unverified accounts (sign-in recovery). Generic response for all outcomes. */
  resendVerificationForSignIn: async (
    email: string
  ): Promise<{
    message: string;
    retry_after_seconds?: number;
    verification_purpose?:
      | "patient_registration"
      | "provider_registration"
      | "social_worker_registration"
      | "provider_staff_registration";
  }> => {
    try {
      const response = await apiClient.post("/auth/resend-verification-sign-in", {
        email,
      });
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) throw error;
      throw new Error(handleAPIError(error));
    }
  },

  sendVerificationCode: async (
    email: string,
    purpose:
      | "provider_registration"
      | "social_worker_registration"
      | "social_worker_invitation"
      | "patient_registration"
      | "provider_staff_registration"
  ): Promise<{
    message: string;
    retry_after_seconds?: number;
    verification_purpose?: string;
    delivery_failed?: boolean;
  }> => {
    try {
      const response = await apiClient.post("/auth/send-verification-code", {
        email,
        purpose,
      });
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        throw error;
      }
      throw new Error(handleAPIError(error));
    }
  },

  // Verify 6-digit code; returns verification_token (JWT) to send when completing registration
  verifyEmail: async (
    email: string,
    code: string,
    purpose:
      | "provider_registration"
      | "social_worker_registration"
      | "social_worker_invitation"
      | "patient_registration"
      | "provider_staff_registration"
  ): Promise<{
    verified: boolean;
    message?: string;
    verification_token?: string;
    account_ready_for_login?: boolean;
  }> => {
    try {
      const response = await apiClient.post("/auth/verify-email", {
        email,
        code,
        purpose,
      });
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) throw error;
      throw new Error(handleAPIError(error));
    }
  },

  // Verify using one-time magic-link token (accessibility: click link instead of typing code)
  verifyEmailByToken: async (
    token: string
  ): Promise<{
    verified: boolean;
    message?: string;
    verification_token?: string;
    account_ready_for_login?: boolean;
  }> => {
    try {
      const response = await apiClient.post("/auth/verify-email", { token });
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) throw error;
      throw new Error(handleAPIError(error));
    }
  },

  // Register social worker
  registerSocialWorker: async (userData: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    role: string;
    email_verification_token?: string;
  }): Promise<any> => {
    try {
      const response = await apiClient.post(
        "/auth/register/social-worker",
        userData
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) throw error;
      throw new Error(handleAPIError(error));
    }
  },

  registerSocialWorkerAgency: async (
    signupData: CareCoordinationAgencyRegistrationRequest,
    idempotencyKey: string
  ): Promise<CareCoordinationAgencyRegistrationResponse> => {
    try {
      const response = await apiClient.post(
        "/auth/register/social-worker-agency",
        signupData,
        { headers: { "Idempotency-Key": idempotencyKey } }
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) throw error;
      throw new Error(handleAPIError(error));
    }
  },

  // Register provider
  registerProvider: async (providerData: {
    password: string;
    first_name: string;
    last_name: string;
    business_name: string;
    business_type: string;
    license_number: string;
    state: string;
    business_email: string;
    address: string;
    city: string;
    zip_code: string;
    phone: string;
    email_verification_token?: string;
  }): Promise<any> => {
    try {
      const response = await apiClient.post(
        "/auth/register/provider",
        providerData
      );
      return response.data;
    } catch (error) {
      throw new Error(handleAPIError(error));
    }
  },

  // Register provider with selection (for multiple matches)
  registerProviderWithSelection: async (
    providerData: {
      password: string;
      first_name: string;
      last_name: string;
      business_name: string;
      business_type: string;
      license_number: string;
      state: string;
      business_email: string;
      address: string;
      city: string;
      zip_code: string;
      phone: string;
      email_verification_token?: string;
    },
    selectedProviderId: number
  ): Promise<any> => {
    try {
      const response = await apiClient.post("/auth/register/provider/select", {
        ...providerData,
        selected_provider_id: selectedProviderId,
      });
      return response.data;
    } catch (error) {
      throw new Error(handleAPIError(error));
    }
  },

  /** Org-wide registration progress for provider accounts (multi-location wizard). */
  getProviderRegistrationStatus: async (): Promise<RegistrationStatus> => {
    try {
      const response = await apiClient.get<RegistrationStatus>(
        "/auth/provider/registration-status"
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        throw error;
      }
      throw new Error(handleAPIError(error));
    }
  },

  // Get current user
  getCurrentUser: async (): Promise<any> => {
    try {
      const response = await apiClient.get("/auth/me");
      return response.data;
    } catch (error) {
      throw new Error(handleAPIError(error));
    }
  },

  /** Active org/facility + resolved Layer 2 permissions (RBAC Phase 2/4). */
  getOrgContext: async (): Promise<
    import("../types/orgContext").OrgContextResponse
  > => {
    try {
      const response = await apiClient.get("/auth/context");
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        throw error;
      }
      throw new Error(handleAPIError(error));
    }
  },

  /** Persist active org/facility context server-side; returns refreshed permissions. */
  putOrgContext: async (
    update: import("../types/orgContext").OrgContextUpdate
  ): Promise<import("../types/orgContext").OrgContextResponse> => {
    try {
      const response = await apiClient.put("/auth/context", update);
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        throw error;
      }
      throw new Error(handleAPIError(error));
    }
  },

  /** Phase 5: public invitation preview (no auth). */
  previewOrgInvitation: async (
    token: string
  ): Promise<import("../types/orgTeam").OrgInvitationPreview> => {
    try {
      const response = await apiClient.get("/auth/invitations/preview", {
        params: { token },
      });
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        throw error;
      }
      throw new Error(handleAPIError(error));
    }
  },

  /** Phase 5: accept invitation for the signed-in user. */
  acceptOrgInvitation: async (
    token: string
  ): Promise<import("../types/orgTeam").OrgTeamMember> => {
    try {
      const response = await apiClient.post("/auth/invitations/accept", {
        token,
      });
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        throw error;
      }
      throw new Error(handleAPIError(error));
    }
  },

  // Logout - clears httpOnly cookies on backend
  logout: async (signal?: AbortSignal): Promise<void> => {
    try {
      await apiClient.post("/auth/logout", undefined, { signal });
      // Backend clears all authentication cookies (access_token, refresh_token, csrf_token)
      // No need to manually clear tokens - cookies are handled by browser
    } catch (error) {
      // Even if API logout fails, cookies will expire naturally
      throw new Error(handleAPIError(error));
    }
  },
};
